import {
  type Component,
  type MaybeRefOrGetter,
  onServerPrefetch,
  shallowRef,
  toValue,
  watch,
} from 'vue'

import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { iconModules } from '@ds/icons/backmarket.icons'

function keyExists(icon: string): icon is keyof typeof iconModules {
  return Object.keys(iconModules).includes(icon)
}

async function importIconComponent(icon: string): Promise<Component> {
  if (!keyExists(icon)) {
    throw new Error(`[CMS Icon] Error when rendering icon ${icon}`)
  }

  const importIconFunction = iconModules[icon]
  const iconModule = (await importIconFunction()) as {
    [icon: string]: Component
  }

  return iconModule[icon]
}

export function useIcon(iconName: MaybeRefOrGetter<string>) {
  const iconComponent = shallowRef<Component>()

  const logger = useLogger()

  onServerPrefetch(async () => {
    await fetchIconComponent()
  })

  watch([iconName], fetchIconComponent)

  async function fetchIconComponent() {
    try {
      iconComponent.value = await importIconComponent(toValue(iconName))
    } catch (err) {
      logger.error(
        `[CMS Icon] Error when rendering icon ${toValue(iconName)}`,
        {
          error: err as Error,
          owners: ['bot-squad-content-front'],
        },
      )
    }
  }

  return { iconComponent, fetchIconComponent }
}
